import { DropdownItem } from '../Dropdown.tsx';
import {
  Specialty,
  Ehr,
  Role,
  HereAs,
  HeardAboutFrom,
  Credential,
  OrganizationType,
  Affiliation,
} from '../../types.ts';
import { FULLY_SUPPORTED_SPECIALTIES } from '../../constants.ts';

export const specialties: Record<Specialty, string> = {
  allergy_and_immunology: 'Allergy and immunology',
  cardiology: 'Cardiology',
  dermatology: 'Dermatology',
  endocrinology: 'Endocrinology',
  gastroenterology: 'Gastroenterology',
  obstetrics_and_gynecology: 'Obstetrics and gynecology',
  hepatology: 'Hepatology',
  internal_medicine: 'Internal medicine',
  neurology: 'Neurology',
  oncology: 'Oncology',
  ophthalmology: 'Ophthalmology',
  pediatrics: 'Pediatrics',
  primary_care: 'Primary care',
  psychiatry: 'Psychiatry',
  pulmonology: 'Pulmonology',
  rheumatology: 'Rheumatology',
  urology: 'Urology',
  multi_specialty: 'Multi-specialty',
  none: 'None',
  other: 'Other',
};

const specialtyKeys = Object.keys(specialties) as Specialty[];

export const getSpecialtyOptions = () =>
  specialtyKeys.map((specialtyKey) => {
    const specialtyValue = specialties[specialtyKey];

    return (
      <DropdownItem key={specialtyKey} value={specialtyKey}>
        {specialtyValue}
      </DropdownItem>
    );
  });

const ehrs: Record<Ehr, string> = {
  advancedmd: 'AdvancedMD',
  athena: 'athenahealth',
  cerner: 'Cerner',
  curemd: 'CureMD',
  drchrono: 'DrChrono',
  eclinicalworks: 'eClinicalWorks',
  elation: 'Elation',
  ema_modmed: 'EMA (ModMed)',
  epic: 'Epic',
  ezderm: 'EZDerm',
  ggastro_modmed: 'gGastro (ModMed)',
  greenway: 'Greenway',
  iclinic_mdland: 'iClinic (MDLand)',
  next_gen: 'NextGen',
  nextech: 'NexTech',
  practice_fusion: 'Practice Fusion',
  praxis: 'Praxis',
  veradigm: 'Veradigm',
  other: 'Other',
  none: 'None',
};

const ehrKeys = Object.keys(ehrs) as Ehr[];

export const getEhrOptions = () =>
  ehrKeys.map((ehrKey) => {
    const ehrValue = ehrs[ehrKey];

    return (
      <DropdownItem key={ehrKey} value={ehrKey}>
        {ehrValue}
      </DropdownItem>
    );
  });

export const roles: Record<Role, string> = {
  administrative_assistant: 'Administrative Assistant',
  medical_assistant: 'Medical Assistant',
  nurse: 'Nurse',
  nurse_practitioner: 'Nurse Practitioner',
  office_manager: 'Office Manager',
  physician: 'Physician',
  physician_assistant: 'Physician Assistant',
  practice_manager: 'Practice Manager',
  prior_authorization_specialist: 'Biologics Coordinator / PA Specialist',
  it_admin: 'IT Admin',
  executive: 'Executive',
  other: 'Other',
};

const roleKeys = Object.keys(roles) as Role[];

export const getRoleOptions = () =>
  roleKeys.map((roleKey) => {
    const roleValue = roles[roleKey];

    return (
      <DropdownItem key={roleKey} value={roleKey}>
        {' '}
        {roleValue}{' '}
      </DropdownItem>
    );
  });

const affiliations: Record<Affiliation, string> = {
  healthcare_practice: 'Healthcare practice',
  patient: 'Patient',
  pharmaceutical_company: 'Pharmaceutical company',
  insurance_company: 'Insurance company',
  pharmacy: 'Pharmacy',
};

const affiliationKeys = Object.keys(affiliations) as Affiliation[];

export const getAffiliationOptions = () =>
  affiliationKeys.map((affiliationKey) => {
    const affiliationValue = affiliations[affiliationKey];

    return (
      <DropdownItem key={affiliationKey} value={affiliationKey}>
        {affiliationValue}
      </DropdownItem>
    );
  });

const credentials: Record<Credential, string> = {
  md: 'MD',
  do: 'DO',
  pa: 'PA',
  np: 'NP',
  rn: 'RN',
  other: 'Other',
  none: 'None',
};

const credentialsKeys = Object.keys(credentials) as Credential[];

export const getCredentialsOptions = () =>
  credentialsKeys.map((credentialOption) => {
    return (
      <DropdownItem key={credentialOption} value={credentialOption}>
        {' '}
        {credentials[credentialOption]}{' '}
      </DropdownItem>
    );
  });

const hereAs: Record<HereAs, string> = {
  prescriber: 'I am a prescriber',
  prescriber_delegate: 'I am a prescriber delegate',
  patient: 'I am a patient',
  pharmaceutical_company: 'I work for a pharmaceutical company',
  insurance_plan: 'I work for an insurance plan',
  pharmacy: 'I work at a pharmacy',
};

const hereAsKeys = Object.keys(hereAs) as HereAs[];

export const getHereAsOptions = () =>
  hereAsKeys.map((hereAsOption) => {
    return (
      <DropdownItem key={hereAsOption} value={hereAsOption}>
        {' '}
        {hereAs[hereAsOption]}{' '}
      </DropdownItem>
    );
  });

const heardAboutFrom: Record<HeardAboutFrom, string> = {
  my_practice: 'Someone from my practice or group',
  another_practice: 'Someone from another practice',
  pharmaceutical_rep: 'Pharmaceutical rep or employee',
  tandem: 'Someone from Tandem',
  event: 'Conference / event',
  pharmacy: 'A pharmacy',
  patient: 'A patient',
  search_engine: 'Search engine (e.g., Google, ChatGPT) (please specify)',
  social_media: 'Social media (e.g., Facebook, LinkedIn) (please specify)',
  physical_mail: 'Physical mail',
  other: 'Other (please specify)',
};

const heardAboutFromKeys = Object.keys(heardAboutFrom) as HeardAboutFrom[];

export const specifyOtherHeardAboutFromOptions: HeardAboutFrom[] = [
  'event',
  'pharmacy',
  'another_practice',
  'search_engine',
  'social_media',
  'other',
];
export const requiredSpecifyOtherHeardAboutFromOptions: HeardAboutFrom[] = [
  'event',
  'search_engine',
  'social_media',
  'other',
];

export const getHeardAboutFromOptions = () =>
  heardAboutFromKeys.map((heardAboutFromOption) => {
    return (
      <DropdownItem key={heardAboutFromOption} value={heardAboutFromOption}>
        {' '}
        {heardAboutFrom[heardAboutFromOption]}{' '}
      </DropdownItem>
    );
  });

const organizationTypes: Record<OrganizationType, string> = {
  independent: 'No, independent or solo practice',
  part_of_midmarket_group: 'Yes, part of a mid-sized group',
  part_of_enterprise_group: 'Yes, part of a large enterprise',
};

const organizationTypeKeys = Object.keys(organizationTypes) as OrganizationType[];

export const getOrganizationTypeOptions = () =>
  organizationTypeKeys.map((organizationTypeKey) => {
    return (
      <DropdownItem key={organizationTypeKey} value={organizationTypeKey}>
        {organizationTypes[organizationTypeKey]}
      </DropdownItem>
    );
  });

export type SignupType = 'smb' | 'midmarket' | 'enterprise';

const SMB_PROVIDER_NUM_MAX = 5;
const MIDMARKET_PROVIDER_NUM_MAX = 50;

export const getSignupType = (size: number, organizationType: OrganizationType): SignupType => {
  if (organizationType === 'independent') {
    if (size <= SMB_PROVIDER_NUM_MAX) {
      return 'smb';
    } else if (size <= MIDMARKET_PROVIDER_NUM_MAX) {
      return 'midmarket';
    } else {
      return 'enterprise';
    }
  } else if (organizationType === 'part_of_midmarket_group') {
    return 'midmarket';
  } else {
    return 'enterprise';
  }
};

export const getRequiredErrorMessage = (label: string): string => {
  return `${label} is required`;
};

// Prompts a confirm dialog when the user tries to leave the page to prevent data loss
export const createBeforeUnloadHandler = (onBeforeUnload?: (formData: any) => void, getValues?: () => any) => {
  return (event: BeforeUnloadEvent) => {
    event.preventDefault();
    if (onBeforeUnload && getValues) {
      onBeforeUnload(getValues());
    }
    return '';
  };
};

/**
 * List of free email domains that trigger the practice website field.
 * These are domains commonly used for personal email accounts rather than
 * business/practice email addresses.
 */
export const FREE_EMAIL_DOMAINS = [
  'aara.care',
  'aol.com',
  'att.net',
  'bellsouth.net',
  'comcast.net',
  'earthlink.net',
  'gmail.com',
  'hotmail.com',
  'hotmail.es',
  'icloud.com',
  'live.com',
  'me.com',
  'msn.com',
  'netzero.net',
  'outlook.com',
  'pacbell.net',
  'proton.me',
  'verizon.net',
  'yahoo.com',
  'ymail.com',
];

/**
 * Checks if the given email address uses a free email domain.
 * Used to determine if the practice website field should be shown.
 */
export const isFreeEmailDomain = (email?: string): boolean => {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? FREE_EMAIL_DOMAINS.includes(domain) : false;
};

/**
 * Utility function to determine if a user can set up an account based on feature flags,
 * signup type, and specialty. Used to check if the user should continue with
 * the full signup flow or be directed to the success page.
 */
export const canSetUpAccount = (
  featureFlags: any,
  signupType: SignupType,
  specialty: Specialty | undefined,
): boolean => {
  // If feature flags are not available, default to false
  if (!featureFlags) {
    return false;
  }

  // If signup flow is not enabled, user cannot set up account
  if (!featureFlags.providerSignupFlow) {
    return false;
  }

  // Enterprise users cannot set up accounts through this flow
  if (signupType === 'enterprise') {
    return false;
  }

  // If we don't have specialty, cannot determine eligibility
  if (!specialty) {
    return false;
  }

  // Only fully supported specialties can set up accounts
  return FULLY_SUPPORTED_SPECIALTIES.includes(specialty as any);
};
